import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as strings from '@hydra/utils/strings';

import Criterium from '@hydra/common/criterium/Criterium';
import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import CriteriumSnippetElement from '@hydra/common/criterium/CriteriumSnippetElement';
import Translate, { useI18n } from '@hydra/common/components/i18n';
import CriteriumIconAndText from '@hydra/common/criterium/CriteriumIconAndText';
import { makeStyles } from '@material-ui/core/styles';
import SafeAnchor from '@hydra/common/components/SafeAnchor';
import { useReviewContext } from '@hydra/common/context/review';
import { getURLDisplayName } from '@hydra/utils/urlUtil';

const MAX_AMOUNT_TO_SHOW = 5;
const THRESHOLD_SCORE_TO_PASS = 0.9;

const useStyles = makeStyles(theme => ({
  showElements: {
    color: theme.palette.primary.darker,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  table: {
    paddingBottom: theme.spacing(3)
  }
}));

export const UnsizedImagesTable = ({ nodes }) => {
  const head = [<Translate content='image_optimization_element' key='element' />];
  const rows = nodes.map(({ snippet, nodeLabel }, i) => {
    return [<CriteriumSnippetElement key={`element-${i}`} snippet={snippet} nodeLabel={nodeLabel} />];
  });
  return <CriteriumTable head={head} rows={rows} showAmount={5} striped />;
};

export const ImagesTable = ({ nodes }) => {
  const { numeral } = useI18n();
  const head = [
    <Translate content='image_optimization_image_url' key='url' />,
    <Translate content='image_optimization_image_size' key='size' />,
    <Translate content='image_optimization_potential_savings' key='savings' />
  ];
  const rows = nodes.map(({ url, wastedBytes, totalBytes }) => {
    const urlDisplayName = getURLDisplayName(url, { preserveHost: true });
    return [
      <SafeAnchor href={url} rel='nofollow noopener noreferrer' target='_blank' key={url}>{urlDisplayName}</SafeAnchor>,
      numeral(totalBytes || 0).format('0.0 ib'),
      numeral(wastedBytes || 0).format('0.0 ib')
    ];
  });

  return (
    <CriteriumTable
      head={head}
      rows={rows}
      showAmount={5}
      fixedColumns={[75, 25, 25]}
      striped
    />
  );
};

const TABLES = {
  unsizedImages: UnsizedImagesTable,
  usesOptimizedImages: ImagesTable,
  usesWebpImages: ImagesTable,
  usesResponsiveImages: ImagesTable,
  offscreenImages: ImagesTable
};

const SORTED_CHECKS = [
  'usesOptimizedImages',
  'usesWebpImages',
  'usesResponsiveImages',
  'offscreenImages',
  'unsizedImages'
];

export const ImageOptimization = ({ criterium }) => {
  const { isPdf } = useReviewContext();

  const classes = useStyles();
  const { data, type } = criterium;
  const {
    unsizedImages,
    usesResponsiveImages,
    usesOptimizedImages,
    usesWebpImages,
    offscreenImages
  } = data;

  const initialState = Object.keys(data).reduce((result, prop) => {
    result[prop] = false;
    return result;
  }, {});
  const [expanded, setExpanded] = useState(initialState);
  const handleClick = name => event => {
    setExpanded({
      ...expanded,
      [name]: !expanded[name]
    });
    event.stopPropagation();
  };

  const count = SORTED_CHECKS.length;
  const passed = (
    unsizedImages.score >= THRESHOLD_SCORE_TO_PASS &&
    usesResponsiveImages.score >= THRESHOLD_SCORE_TO_PASS &&
    usesWebpImages.score >= THRESHOLD_SCORE_TO_PASS &&
    usesOptimizedImages.score >= THRESHOLD_SCORE_TO_PASS &&
    offscreenImages.score >= THRESHOLD_SCORE_TO_PASS
  );

  const textMessage = passed ? 'image_optimization_summary_ok' : 'image_optimization_summary_not_ok';

  const { passedChecks, failedChecks } = SORTED_CHECKS.reduce((result, check) => {
    if (data[check].score >= THRESHOLD_SCORE_TO_PASS) {
      result.passedChecks.push(check);
    } else {
      result.failedChecks.push(check);
    }
    return result;
  }, { passedChecks: [], failedChecks: [] });

  const content = (
    <>
      <Translate content={textMessage} with={{ count }} />
      {passedChecks.map((check, i) => {
        const content = `${type}_${strings.snakeCase(check)}`.toLowerCase();
        return <CriteriumIconAndText key={i} success text={<Translate content={content} />} />;
      })}

      {failedChecks.map((check, i) => {
        const content = `${type}_${strings.snakeCase(check)}_bad`.toLowerCase();
        const text = (
          <>
            <Translate content={content} />
            {!isPdf && (
              <div onClick={handleClick(check)} className={classes.showElements}>
                <Translate content={!expanded[check] ? 'show_elements' : 'hide_elements'} />
              </div>
            )}
          </>
        );

        const Table = TABLES[check];

        return (
          <div key={i}>
            <CriteriumIconAndText success={false} text={text} />
            {
              !isPdf && expanded[check] &&
                <div className={classes.table}>
                  <Table nodes={data[check].nodes.slice(0, MAX_AMOUNT_TO_SHOW)} />
                  {
                    data[check].nodes.length > MAX_AMOUNT_TO_SHOW &&
                      <Translate content='pdf_and_more' with={{ count: data[check].nodes.length - MAX_AMOUNT_TO_SHOW }} />
                  }
                </div>
            }
          </div>
        );
      })}
    </>
  );

  return <Criterium criterium={criterium} variables={{ count }}>{content}</Criterium>;
};

ImageOptimization.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      unsizedImages: PropTypes.shape({
        score: PropTypes.number,
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            snippet: PropTypes.string,
            nodeLabel: PropTypes.string
          })
        )
      }),
      usesResponsiveImages: PropTypes.shape({
        score: PropTypes.number,
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            wastedBytes: PropTypes.number,
            totalBytes: PropTypes.number
          })
        )
      }),
      usesWebpImages: PropTypes.shape({
        score: PropTypes.number,
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            wastedBytes: PropTypes.number,
            totalBytes: PropTypes.number
          })
        )
      }),
      usesOptimizedImages: PropTypes.shape({
        score: PropTypes.number,
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            wastedBytes: PropTypes.number,
            totalBytes: PropTypes.number
          })
        )
      }),
      offscreenImages: PropTypes.shape({
        score: PropTypes.number,
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            wastedBytes: PropTypes.number,
            totalBytes: PropTypes.number
          })
        )
      })
    })
  })
};

export default React.memo(ImageOptimization);
